// src/hooks/useDistanceCalibration.js

import { useMemo } from "react";
import {
  applyEmpiricalCalibration,
  calculateWaterLevel,
  calibratedDistanceFromDuration,
  estimateDuration,
} from "../utils/calibrationUtils";

/**
 * Custom hook for processing sensor readings with calibration
 *
 * @param {Array} readings - Raw sensor readings from API
 * @param {Object} deviceConfig - Device calibration configuration
 * @returns {Object} Enhanced data with all calibration stages
 */
export const useDistanceCalibration = (readings, deviceConfig) => {
  const enhancedData = useMemo(() => {
    if (!readings || !Array.isArray(readings) || !deviceConfig) {
      return [];
    }

    return readings
      .map((item, index) => {
        // Process Sensor 1
        const sensor1 = processSensorData(
          item.distance_1,
          item.distance_1_calibrated,
          item.temperature,
          item.humidity,
          item.pressure,
          deviceConfig.distance_1_offset,
        );

        // Process Sensor 2
        const sensor2 = processSensorData(
          item.distance_2,
          item.distance_2_calibrated,
          item.temperature,
          item.humidity,
          item.pressure,
          deviceConfig.distance_2_offset,
        );

        // Calculate average distance
        const avgDistance = calculateAverageDistance(
          item.distance_1,
          item.distance_2,
        );

        return {
          ...item,
          // Sensor 1 calibrated distances
          distance_1_empirical: sensor1.empirical,

          // Sensor 2 calibrated distances
          distance_2_empirical: sensor2.empirical,

          // Water levels for Sensor 1
          waterLevel1: sensor1.waterLevel.raw,
          waterLevel1Calibrated: sensor1.waterLevel.calibrated,
          waterLevel1Empirical: sensor1.waterLevel.empirical,

          // Water levels for Sensor 2
          waterLevel2: sensor2.waterLevel.raw,
          waterLevel2Calibrated: sensor2.waterLevel.calibrated,
          waterLevel2Empirical: sensor2.waterLevel.empirical,

          // Metadata
          avgDistance,
          timeNumeric: item.timestamp
            ? new Date(item.timestamp).getTime()
            : index,
          time: formatTimestamp(item.timestamp, index),
        };
      })
      .sort((a, b) => sortByTimestamp(a, b));
  }, [readings, deviceConfig]);

  const latestData = useMemo(() => {
    if (!enhancedData || enhancedData.length === 0) return null;
    return enhancedData[enhancedData.length - 1];
  }, [enhancedData]);

  const statistics = useMemo(() => {
    return calculateStatistics(enhancedData);
  }, [enhancedData]);

  return {
    enhancedData,
    latestData,
    statistics,
  };
};

/**
 * Process a single sensor's data through all calibration stages
 */
const processSensorData = (
  rawDistance,
  calibratedDistance,
  temperature,
  humidity,
  pressure,
  offset,
) => {
  // If backend already provides calibrated distance, use it
  // Otherwise calculate it
  const physicsCalibrated =
    calibratedDistance ??
    (rawDistance && temperature && humidity && pressure
      ? calibratedDistanceFromDuration(
          estimateDuration(rawDistance),
          temperature,
          humidity,
          pressure * 100.0, // Convert hPa to Pa
        )
      : null);

  // Apply empirical correction
  const empiricalCalibrated = applyEmpiricalCalibration(
    physicsCalibrated,
    temperature,
  );

  return {
    raw: rawDistance,
    physicsCalibrated,
    empirical: empiricalCalibrated,
    waterLevel: {
      raw: calculateWaterLevel(rawDistance, offset),
      calibrated: calculateWaterLevel(physicsCalibrated, offset),
      empirical: calculateWaterLevel(empiricalCalibrated, offset),
    },
  };
};

/**
 * Calculate average distance from two sensors
 */
const calculateAverageDistance = (distance1, distance2) => {
  if (distance1 && distance2) {
    return (distance1 + distance2) / 2;
  }
  return distance1 || distance2 || 0;
};

/**
 * Format timestamp for display
 */
const formatTimestamp = (timestamp, fallbackIndex) => {
  if (!timestamp) {
    return `Point ${fallbackIndex + 1}`;
  }

  return new Date(timestamp).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

/**
 * Sort function for timestamp-based sorting
 */
const sortByTimestamp = (a, b) => {
  if (!a.timestamp && !b.timestamp) return 0;
  if (!a.timestamp) return 1;
  if (!b.timestamp) return -1;
  return new Date(a.timestamp) - new Date(b.timestamp);
};

/**
 * Calculate statistics across the dataset
 */
const calculateStatistics = (data) => {
  if (!data || data.length === 0) {
    return null;
  }

  const validData = data.filter((d) => d.distance_1_empirical != null);

  if (validData.length === 0) {
    return null;
  }

  const distances = validData.map((d) => d.distance_1_empirical);

  return {
    count: validData.length,
    mean: distances.reduce((a, b) => a + b, 0) / distances.length,
    min: Math.min(...distances),
    max: Math.max(...distances),
    stdDev: calculateStdDev(distances),
  };
};

/**
 * Calculate standard deviation
 */
const calculateStdDev = (values) => {
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const squaredDiffs = values.map((value) => Math.pow(value - mean, 2));
  const variance = squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
  return Math.sqrt(variance);
};

export default useDistanceCalibration;
